import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import StudioSidebar from "./StudioSidebar";
import StudioOnboardingTour from "./StudioOnboardingTour";

import HookTool from "./tools/HookTool";
import HookAngleTool from "./tools/HookAngleTool";
import TitleTool from "./tools/TitleTool";
import CaptionTool from "./tools/CaptionTool";
import HashtagTool from "./tools/HashtagTool";
import DescriptionTool from "./tools/DescriptionTool";
import ScriptTool from "./tools/ScriptTool";
import CTATool from "./tools/CTATool";
import ThumbnailTool from "./tools/ThumbnailTool";
import AIHumanizerTool from "./tools/AIHumanizerTool";
import ContentRepurposerTool from "./tools/ContentRepurposerTool";
import AdCopyTool from "./tools/AdCopyTool";
import CarouselGeneratorTool from "./tools/CarouselGeneratorTool";
import ViralRewriterTool from "./tools/ViralRewriterTool";
import OfferPositioningTool from "./tools/OfferPositioningTool";
import HookScoreAnalyzerTool from "./tools/HookScoreAnalyzerTool";
import TrendToContentTool from "./tools/TrendToContentTool";
import NigerianAudienceRewriteTool from "./tools/NigerianAudienceRewriteTool";
import RetentionIntroTool from "./tools/RetentionIntroTool";
import BrandVoiceTrainerTool from "./tools/BrandVoiceTrainerTool";
import ContentDifferentiationTool from "./tools/ContentDifferentiationTool";
import CommentToContentTool from "./tools/CommentToContentTool";
import useI18n from "../../i18n/useI18n";
import MotionSurface from "../../motion/studio/MotionSurface";

const TOUR_STORAGE_KEY = "hookora_studio_tour_seen";
const THEME_KEY = "hookora_theme";

const PLAN_ORDER = {
  free: 0,
  creator: 1,
  pro: 2,
};

const TOOL_MIN_PLAN = {
  hooks: "free",
  titles: "free",
  captions: "free",
  hashtags: "free",
  descriptions: "free",
  scripts: "free",
  hook_angles: "creator",
  cta: "creator",
  thumbnail: "creator",
  humanizer: "creator",
  repurpose: "creator",
  ad_copy: "creator",
  carousels: "creator",
  viral_rewrite: "creator",
  positioning: "creator",
  hook_score: "creator",
  nigerian_rewrite: "creator",
  retention_intro: "creator",
  comment_to_content: "creator",
  trend_to_content: "pro",
  brand_voice_trainer: "pro",
  content_differentiation: "pro",
};

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const getStoredUserPlan = () => {
  if (typeof window === "undefined") return "free";

  try {
    const raw = localStorage.getItem("user");
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.plan || "free";
  } catch {
    return "free";
  }
};

const normalizePlan = (plan) => {
  const cleaned = String(plan || "free").toLowerCase();
  return Object.prototype.hasOwnProperty.call(PLAN_ORDER, cleaned) ? cleaned : "free";
};

const getRequiredPlan = (toolKey) => TOOL_MIN_PLAN[toolKey] || "creator";

const canAccessTool = (currentPlan, toolKey) =>
  PLAN_ORDER[normalizePlan(currentPlan)] >= PLAN_ORDER[getRequiredPlan(toolKey)];

const StudioLayout = () => {
  const { t } = useI18n();

  const [theme, setTheme] = useState(getStoredTheme());
  const [currentPlan, setCurrentPlan] = useState(getStoredUserPlan());
  const [activeTool, setActiveTool] = useState("hooks");
  const [sharedTopic, setSharedTopic] = useState("");
  const [sharedPlatform, setSharedPlatform] = useState("youtube");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 900 : false
  );

  const [tourOpen, setTourOpen] = useState(false);
  const [activeTourStep, setActiveTourStep] = useState(0);
  const [targetRects, setTargetRects] = useState({});
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [lockedTool, setLockedTool] = useState(null);

  const pageHeaderRef = useRef(null);
  const replayButtonRef = useRef(null);
  const toolsRailRef = useRef(null);
  const workspaceRef = useRef(null);
  const outputRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    const syncUserPlan = () => {
      setCurrentPlan(getStoredUserPlan());
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("storage", syncUserPlan);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);
    window.addEventListener("hookora-auth-updated", syncUserPlan);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("storage", syncUserPlan);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
      window.removeEventListener("hookora-auth-updated", syncUserPlan);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isDark = theme === "dark";
  const normalizedPlan = normalizePlan(currentPlan);

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%), linear-gradient(180deg, rgba(2,6,23,0.00), rgba(2,6,23,0.10))"
        : "radial-gradient(circle at top left, rgba(99,102,241,0.08), transparent 26%), radial-gradient(circle at top right, rgba(59,130,246,0.06), transparent 22%)",
      title: isDark ? "#f8fafc" : "#0f172a",
      muted: isDark ? "#94a3b8" : "#64748b",
      headerChipBg: isDark ? "rgba(99,102,241,0.14)" : "#eef2ff",
      headerChipBorder: isDark ? "rgba(129,140,248,0.22)" : "#c7d2fe",
      headerChipText: isDark ? "#c7d2fe" : "#4338ca",
      replayBg: isDark ? "rgba(15,23,42,0.96)" : "#ffffff",
      replayBorder: isDark ? "rgba(148,163,184,0.24)" : "#d1d5db",
      replayText: isDark ? "#e2e8f0" : "#111827",
      replayShadow: isDark
        ? "0 10px 24px rgba(2,6,23,0.28)"
        : "0 8px 18px rgba(15,23,42,0.06)",
      workspaceBg: isDark
        ? "linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.84))"
        : "linear-gradient(180deg, #ffffff, #fcfdff)",
      workspaceBorder: isDark ? "rgba(148,163,184,0.16)" : "#e2e8f0",
      workspaceShadow: isDark
        ? "0 18px 40px rgba(2, 6, 23, 0.38)"
        : "0 14px 34px rgba(15, 23, 42, 0.08)",
      innerGlow: isDark
        ? "inset 0 1px 0 rgba(255,255,255,0.03)"
        : "inset 0 1px 0 rgba(255,255,255,0.75)",
      premiumCardBg: isDark ? "rgba(120,53,15,0.14)" : "#fff7ed",
      premiumCardBorder: isDark ? "rgba(251,191,36,0.22)" : "#fdba74",
      premiumCardText: isDark ? "#fde68a" : "#9a3412",
      creatorCardBg: isDark ? "rgba(37,99,235,0.12)" : "#eff6ff",
      creatorCardBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
      creatorCardText: isDark ? "#bfdbfe" : "#1d4ed8",
      proCardBg: isDark ? "rgba(109,40,217,0.14)" : "#f5f3ff",
      proCardBorder: isDark ? "rgba(216,180,254,0.22)" : "#d8b4fe",
      proCardText: isDark ? "#e9d5ff" : "#7c3aed",
      modalOverlay: isDark ? "rgba(2,6,23,0.72)" : "rgba(15,23,42,0.45)",
      modalBg: isDark
        ? "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.94) 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
      modalBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      modalText: isDark ? "#f8fafc" : "#111827",
      modalMuted: isDark ? "#cbd5e1" : "#374151",
      creatorBadgeBg: isDark ? "rgba(37,99,235,0.16)" : "#dbeafe",
      creatorBadgeBorder: isDark ? "rgba(96,165,250,0.28)" : "#93c5fd",
      creatorBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      proBadgeBg: isDark ? "rgba(109,40,217,0.18)" : "#f3e8ff",
      proBadgeBorder: isDark ? "rgba(216,180,254,0.32)" : "#d8b4fe",
      proBadgeText: isDark ? "#e9d5ff" : "#7c3aed",
    }),
    [isDark]
  );

  const sharedToolProps = useMemo(
    () => ({
      sharedTopic,
      setSharedTopic,
      sharedPlatform,
      setSharedPlatform,
    }),
    [sharedTopic, sharedPlatform]
  );

  const planLabelMap = useMemo(
    () => ({
      free: t("plans.free"),
      creator: t("plans.creator"),
      pro: t("plans.pro"),
    }),
    [t]
  );

  const getTierLabel = (plan) => {
    const normalized = normalizePlan(plan);
    return planLabelMap[normalized] || planLabelMap.free;
  };

  const tourSteps = useMemo(
    () => [
      {
        id: "welcome",
        targetKey: "header",
        title: t("studioLayout.tour.welcome.title"),
        description: t("studioLayout.tour.welcome.description"),
      },
      {
        id: "tools",
        targetKey: "tools",
        title: t("studioLayout.tour.tools.title"),
        description: t("studioLayout.tour.tools.description"),
      },
      {
        id: "workspace",
        targetKey: "workspace",
        title: t("studioLayout.tour.workspace.title"),
        description: t("studioLayout.tour.workspace.description"),
      },
      {
        id: "results",
        targetKey: "results",
        title: t("studioLayout.tour.results.title"),
        description: t("studioLayout.tour.results.description"),
      },
      {
        id: "replay",
        targetKey: "replay",
        title: t("studioLayout.tour.replay.title"),
        description: t("studioLayout.tour.replay.description"),
      },
    ],
    [t]
  );

  const measureTargets = () => {
    const getRect = (ref) => {
      if (!ref?.current) return null;
      const rect = ref.current.getBoundingClientRect();

      return {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      };
    };

    setTargetRects({
      header: getRect(pageHeaderRef),
      replay: getRect(replayButtonRef),
      tools: getRect(toolsRailRef),
      workspace: getRect(workspaceRef),
      results: getRect(outputRef) || getRect(workspaceRef),
    });
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const seenTour = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!seenTour) {
      const timer = window.setTimeout(() => {
        setTourOpen(true);
        setActiveTourStep(0);
      }, 450);

      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, []);

  useEffect(() => {
    measureTargets();

    const handleResize = () => measureTargets();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [activeTool, sharedTopic, sharedPlatform, isMobile]);

  useEffect(() => {
    if (!tourOpen) return;
    measureTargets();
  }, [tourOpen, activeTourStep, activeTool, isMobile]);

  useEffect(() => {
    if (!upgradeModalOpen || typeof document === "undefined") return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [upgradeModalOpen]);

  useEffect(() => {
    if (canAccessTool(normalizedPlan, activeTool)) return;
    setActiveTool("hooks");
  }, [activeTool, normalizedPlan]);

  const openTour = () => {
    setTourOpen(true);
    setActiveTourStep(0);
    window.setTimeout(() => measureTargets(), 50);
  };

  const closeTour = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem(TOUR_STORAGE_KEY, "true");
    }
    setTourOpen(false);
    setActiveTourStep(0);
  };

  const finishTour = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem(TOUR_STORAGE_KEY, "true");
    }
    setTourOpen(false);
    setActiveTourStep(0);
  };

  const nextTourStep = () => {
    setActiveTourStep((prev) => Math.min(prev + 1, tourSteps.length - 1));
  };

  const prevTourStep = () => {
    setActiveTourStep((prev) => Math.max(prev - 1, 0));
  };

  const handleLockedToolAttempt = (tool) => {
    setLockedTool(tool);
    setUpgradeModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setUpgradeModalOpen(false);
    setLockedTool(null);
  };

  const renderActiveTool = () => {
    switch (activeTool) {
      case "hooks":
        return <HookTool {...sharedToolProps} />;

      case "hook_angles":
        return <HookAngleTool {...sharedToolProps} />;

      case "titles":
        return <TitleTool {...sharedToolProps} />;

      case "captions":
        return <CaptionTool {...sharedToolProps} />;

      case "hashtags":
        return <HashtagTool {...sharedToolProps} />;

      case "descriptions":
        return <DescriptionTool {...sharedToolProps} />;

      case "scripts":
        return <ScriptTool {...sharedToolProps} />;

      case "cta":
        return <CTATool {...sharedToolProps} />;

      case "thumbnail":
        return <ThumbnailTool {...sharedToolProps} />;

      case "humanizer":
        return <AIHumanizerTool {...sharedToolProps} />;

      case "repurpose":
        return <ContentRepurposerTool {...sharedToolProps} />;

      case "ad_copy":
        return <AdCopyTool {...sharedToolProps} />;

      case "carousels":
        return <CarouselGeneratorTool {...sharedToolProps} />;

      case "viral_rewrite":
        return <ViralRewriterTool {...sharedToolProps} />;

      case "positioning":
        return <OfferPositioningTool {...sharedToolProps} />;

      case "hook_score":
        return <HookScoreAnalyzerTool {...sharedToolProps} />;

      case "trend_to_content":
        return <TrendToContentTool {...sharedToolProps} />;

      case "nigerian_rewrite":
        return <NigerianAudienceRewriteTool {...sharedToolProps} />;

      case "retention_intro":
        return <RetentionIntroTool {...sharedToolProps} />;

      case "brand_voice_trainer":
        return <BrandVoiceTrainerTool {...sharedToolProps} />;

      case "content_differentiation":
        return <ContentDifferentiationTool {...sharedToolProps} />;

      case "comment_to_content":
        return <CommentToContentTool {...sharedToolProps} />;

      default:
        return <div>{t("studioLayout.selectTool")}</div>;
    }
  };

  const lockedRequiredPlan = lockedTool?.requiredPlan || "creator";
  const lockedTierLabel = lockedTool?.tierLabel || getTierLabel(lockedRequiredPlan);
  const lockedTierColors =
    lockedRequiredPlan === "pro"
      ? {
          background: colors.proBadgeBg,
          border: colors.proBadgeBorder,
          color: colors.proBadgeText,
        }
      : {
          background: colors.creatorBadgeBg,
          border: colors.creatorBadgeBorder,
          color: colors.creatorBadgeText,
        };

  const planNoticeStyles =
    normalizedPlan === "free"
      ? {
          background: colors.premiumCardBg,
          border: colors.premiumCardBorder,
          color: colors.premiumCardText,
          text: t("studioLayout.planNotice.free"),
        }
      : normalizedPlan === "creator"
        ? {
            background: colors.creatorCardBg,
            border: colors.creatorCardBorder,
            color: colors.creatorCardText,
            text: t("studioLayout.planNotice.creator"),
          }
        : null;

  const upgradeBenefitItems = useMemo(
    () => [
      t("studioLayout.upgradeModal.benefits.requiredTier", { tier: lockedTierLabel }),
      t("studioLayout.upgradeModal.benefits.strongerContent"),
      t("studioLayout.upgradeModal.benefits.yearlyValue"),
    ],
    [lockedTierLabel, t]
  );

  return (
    <div
      style={{
        padding: isMobile ? "10px 12px 24px" : "28px 24px 32px",
        background: colors.pageBg,
        backgroundImage: colors.pageGradient,
        minHeight: "calc(100vh - 72px)",
      }}
    >
      <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
        <div
          ref={pageHeaderRef}
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "flex-start",
            gap: isMobile ? "10px" : "18px",
            marginBottom: isMobile ? "12px" : "18px",
          }}
        >
          <div style={{ maxWidth: isMobile ? "100%" : "760px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 10px",
                borderRadius: "999px",
                border: `1px solid ${colors.headerChipBorder}`,
                background: colors.headerChipBg,
                color: colors.headerChipText,
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.02em",
                marginBottom: "10px",
              }}
            >
              {t("studioLayout.headerChip")}
            </div>

            <h1
              style={{
                marginTop: 0,
                marginBottom: "8px",
                color: colors.title,
                fontSize: isMobile ? "28px" : "46px",
                lineHeight: 1.04,
                letterSpacing: "-0.03em",
              }}
            >
              {t("common.studio")}
            </h1>

            <p
              style={{
                color: colors.muted,
                margin: 0,
                lineHeight: "1.6",
                fontSize: isMobile ? "13px" : "16px",
                maxWidth: "760px",
              }}
            >
              {t("studioLayout.description")}
            </p>

            {planNoticeStyles ? (
              <div
                style={{
                  marginTop: "14px",
                  display: "inline-flex",
                  padding: "10px 12px",
                  borderRadius: "14px",
                  background: planNoticeStyles.background,
                  border: `1px solid ${planNoticeStyles.border}`,
                  color: planNoticeStyles.color,
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: 700,
                }}
              >
                {planNoticeStyles.text}
              </div>
            ) : null}
          </div>

          <button
            ref={replayButtonRef}
            type="button"
            onClick={openTour}
            style={{
              padding: "11px 16px",
              borderRadius: "12px",
              border: `1px solid ${colors.replayBorder}`,
              background: colors.replayBg,
              color: colors.replayText,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: colors.replayShadow,
              width: isMobile ? "100%" : "auto",
              alignSelf: isMobile ? "stretch" : "flex-start",
            }}
          >
            {t("studioLayout.replayTour")}
          </button>
        </div>

        <div
          ref={toolsRailRef}
          style={{ marginBottom: isMobile ? "12px" : "16px" }}
        >
          <StudioSidebar
            activeTool={activeTool}
            setActiveTool={setActiveTool}
            isMobile={isMobile}
            currentPlan={normalizedPlan}
            onLockedToolAttempt={handleLockedToolAttempt}
          />
        </div>

        <MotionSurface delay={120}>
        <div
          ref={workspaceRef}
          style={{
            border: `1px solid ${colors.workspaceBorder}`,
            borderRadius: isMobile ? "18px" : "22px",
            background: colors.workspaceBg,
            padding: isMobile ? "12px" : "20px",
            boxShadow: colors.workspaceShadow,
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              borderRadius: "18px",
              boxShadow: colors.innerGlow,
              minWidth: 0,
            }}
          >
            <div ref={outputRef} style={{ minWidth: 0 }}>
              {renderActiveTool()}
            </div>
          </div>
        </div>
        </MotionSurface>
      </div>

      <StudioOnboardingTour
        isOpen={tourOpen}
        steps={tourSteps}
        activeStep={activeTourStep}
        onClose={closeTour}
        onNext={nextTourStep}
        onPrev={prevTourStep}
        onFinish={finishTour}
        targetRects={targetRects}
      />

      {upgradeModalOpen && lockedTool ? (
        <div
          onClick={closeUpgradeModal}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: colors.modalOverlay,
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(560px, 100%)",
              background: colors.modalBg,
              border: `1px solid ${colors.modalBorder}`,
              borderRadius: "24px",
              boxShadow: isDark
                ? "0 28px 80px rgba(2,6,23,0.6)"
                : "0 28px 80px rgba(15,23,42,0.18)",
              padding: isMobile ? "20px" : "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                alignItems: "flex-start",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 12px",
                  borderRadius: "999px",
                  background: lockedTierColors.background,
                  border: `1px solid ${lockedTierColors.border}`,
                  color: lockedTierColors.color,
                  fontSize: "12px",
                  fontWeight: 800,
                }}
              >
                {lockedTierLabel} {t("studioLayout.upgradeModal.studioTool")}
                <span style={{ fontSize: "12px" }}>🔒</span>
              </div>

              <button
                type="button"
                onClick={closeUpgradeModal}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  border: `1px solid ${colors.modalBorder}`,
                  background: isDark ? "rgba(30,41,59,0.88)" : "#ffffff",
                  color: colors.modalText,
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: 800,
                }}
              >
                ✕
              </button>
            </div>

            <h2
              style={{
                margin: "0 0 10px",
                color: colors.modalText,
                fontSize: "clamp(1.8rem, 3vw, 2.3rem)",
                lineHeight: "1.08",
                letterSpacing: "-0.03em",
              }}
            >
              {t("studioLayout.upgradeModal.unlock")} {lockedTool.label}
            </h2>

            <p
              style={{
                margin: "0 0 16px",
                color: colors.modalMuted,
                fontSize: "15px",
                lineHeight: "1.75",
              }}
            >
              {t("studioLayout.upgradeModal.descriptionStart")}{" "}
              <strong>{lockedTierLabel}</strong>{" "}
              {t("studioLayout.upgradeModal.descriptionEnd")}
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
                gap: "10px",
                marginBottom: "18px",
              }}
            >
              {upgradeBenefitItems.map((item) => (
                <div
                  key={item}
                  style={{
                    padding: "12px",
                    borderRadius: "16px",
                    background: isDark ? "rgba(30,41,59,0.72)" : "#ffffff",
                    border: `1px solid ${colors.modalBorder}`,
                    color: colors.modalMuted,
                    fontSize: "13px",
                    lineHeight: "1.6",
                    fontWeight: 700,
                  }}
                >
                  {item}
                </div>
              ))}
            </div>

            <div
              style={{
                padding: "14px 16px",
                borderRadius: "18px",
                marginBottom: "18px",
                background: isDark
                  ? "linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.18))"
                  : "linear-gradient(135deg, #eff6ff, #f5f3ff)",
                border: `1px solid ${isDark ? "rgba(129,140,248,0.24)" : "#c7d2fe"}`,
              }}
            >
              <div
                style={{
                  color: colors.modalText,
                  fontSize: "14px",
                  fontWeight: 800,
                  marginBottom: "4px",
                }}
              >
                {t("studioLayout.upgradeModal.yearlyTitle")}
              </div>

              <div
                style={{
                  color: colors.modalMuted,
                  fontSize: "14px",
                  lineHeight: "1.65",
                }}
              >
                {t("pricing.save17")} <strong>{t("pricing.save17")}</strong>{" "}
                {t("studioLayout.upgradeModal.andGet")}{" "}
                <strong>{t("pricing.twoMonthsFree")}</strong>{" "}
                {t("studioLayout.upgradeModal.yearlyDescriptionEnd")}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <Link
                to="/app/pricing"
                style={{
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textDecoration: "none",
                  background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                  color: "#ffffff",
                  padding: "14px 18px",
                  borderRadius: "14px",
                  fontWeight: 800,
                  boxShadow: "0 12px 28px rgba(79, 70, 229, 0.24)",
                }}
                onClick={closeUpgradeModal}
              >
                {t("studioLayout.upgradeModal.upgradeTo")} {lockedTierLabel}
              </Link>

              <button
                type="button"
                onClick={closeUpgradeModal}
                style={{
                  border: `1px solid ${colors.modalBorder}`,
                  background: isDark ? "rgba(30,41,59,0.88)" : "#ffffff",
                  color: colors.modalText,
                  padding: "14px 18px",
                  borderRadius: "14px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {t("studioLayout.upgradeModal.maybeLater")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StudioLayout;